import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import CategorySection from './components/CategorySection';
import FeaturedSection from './components/FeaturedSection';
import HotRanking from './components/HotRanking';
import FAQSection from './components/FAQSection';
import { featuredContent, hotRanking, faqItems } from '@/mocks/data';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '国产精品在线',
  url: 'https://www.guochangjingpin.com',
  description: '国产精品在线为您汇聚海量正版高清国产内容，涵盖影视、综艺、纪录片等多元品类。',
  foundingDate: '2018',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+86-400-888-9999',
    contactType: 'customer service',
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: '北京',
    addressRegion: '北京',
    addressCountry: 'CN',
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '国产精品在线',
  url: 'https://www.guochangjingpin.com',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.guochangjingpin.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '国产精品在线 - 首页',
  description: '国产精品在线为您汇聚海量正版高清国产内容，涵盖影视、综艺、纪录片等多元品类，支持多端流畅播放。',
  url: 'https://www.guochangjingpin.com',
};

export default function Home() {
  return (
    <>
      <SEOHead
        title="首页"
        description="国产精品在线为您汇聚海量正版高清国产内容，涵盖影视、综艺、纪录片等多元品类，支持多端流畅播放，随时随地享受优质国产文化精品，是您发现和体验顶级国产内容的首选在线平台。"
        keywords="国产精品在线,国产精品在线观看,国产高清在线播放,国产正版内容平台,国产影视在线,精品国产剧集,国产综艺在线"
        canonicalUrl="https://www.guochangjingpin.com"
        schemaData={[organizationSchema, websiteSchema, webPageSchema]}
      />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20entertainment%20streaming%20platform%20hero%20background%2C%20abstract%20colorful%20gradient%20with%20warm%20red%20and%20golden%20tones%2C%20soft%20flowing%20curves%20and%20light%20particles%2C%20elegant%20cinematic%20aesthetic%2C%20clean%20minimal%20design%20with%20sparkle%20effects&width=1800&height=900&seq=hero-home-2025&orientation=landscape"
            alt="国产精品在线 - 正版高清内容聚合平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight">
              发现顶级国产文化精品
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              汇聚海量正版高清内容，涵盖影视、综艺、纪录片等多元品类，随时随地享受优质国产文化
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <div className="relative w-full max-w-md">
                <input
                  type="text"
                  placeholder="搜索您感兴趣的内容..."
                  className="w-full pl-10 pr-4 py-3 rounded-full bg-white/95 text-foreground-900 text-sm placeholder:text-foreground-400 border border-white/30 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-all"
                />
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              </div>
              <button className="px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors">
                立即探索
              </button>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-6">
              <div className="flex items-center gap-1 text-white/70 text-xs">
                <i className="ri-checkbox-circle-fill text-accent-400"></i>
                <span>正版授权</span>
              </div>
              <div className="flex items-center gap-1 text-white/70 text-xs">
                <i className="ri-checkbox-circle-fill text-accent-400"></i>
                <span>高清画质</span>
              </div>
              <div className="flex items-center gap-1 text-white/70 text-xs">
                <i className="ri-checkbox-circle-fill text-accent-400"></i>
                <span>多端同步</span>
              </div>
              <div className="flex items-center gap-1 text-white/70 text-xs">
                <i className="ri-checkbox-circle-fill text-accent-400"></i>
                <span>每日更新</span>
              </div>
            </div>
          </div>
        </section>

        {/* Category Section */}
        <CategorySection />

        {/* Featured Section */}
        <section className="bg-background-100/70">
          <FeaturedSection items={featuredContent} />
        </section>

        {/* Hot Ranking */}
        <HotRanking items={hotRanking} />

        {/* CTA Section */}
        <section className="w-full bg-primary-600">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16 text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              准备好开始探索了吗？
            </h2>
            <p className="text-white/80 mb-6 max-w-lg mx-auto text-sm md:text-base">
              加入数百万用户的行列，发现和享受最优质的国产文化内容
            </p>
            <Link
              to="/category/dianshiju"
              className="inline-flex px-8 py-3 bg-white text-primary-600 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors"
            >
              立即浏览内容
            </Link>
          </div>
        </section>

        {/* FAQ Section */}
        <FAQSection items={faqItems} />
      </main>
    </>
  );
}